import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, of } from 'rxjs';
import { EntNavigation } from 'src/app/navigation/ent-navigation';
import { map } from 'rxjs';
import { DefaultResponse } from 'src/app/models/services/default-reponse.model';
import { Service } from 'src/app/models/service.model';
import { RouteStatus } from 'src/app/models/services/maintenance/routeStatus.model';
import {
  EntMaintenanceService,
  toService,
} from 'src/app/models/services/maintenance/maintenanceService.model';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class RouteStatusService {
  private apiUrlSettings =
    environment.apiUrl + '/Maintenance/maintenanceSettings';
  private apiUrlServices =
    environment.apiUrl + '/Maintenance/maintenanceServices';
  private maintenanceSettingsCache: Observable<RouteStatus[]> | null = null;

  constructor(
    private http: HttpClient,
    private _localStorageService: LocalStorageService
  ) {}

  // Get all routes status from backends
  getMaintenanceServices(): Observable<Service[]> {
    return this.http
      .get<EntMaintenanceService[]>(this.apiUrlServices)
      .pipe(
        catchError(() => of([])) // Handle error and return an empty array
      )
      .pipe(
        map((services: EntMaintenanceService[]) => {
          return services.map(s => toService(s));
        })
      );
  }

  // Get all routes status from backends
  getRouteStatuses(): Observable<RouteStatus[]> {
    // Try to get cached data from localStorage
    const cachedData = this._localStorageService.getItem<RouteStatus[]>(
      'maintenanceSettings'
    );
    if (cachedData) {
      return of(cachedData); // Return cached data
    }

    if (!this.maintenanceSettingsCache) {
      this.maintenanceSettingsCache = this.http
        .get<RouteStatus[]>(this.apiUrlSettings)
        .pipe(
          shareReplay(1), // Cache the response for future subscribers to prevent multiple api calls
          tap(routeStatuses => {
            this._localStorageService.setItem(
              'maintenanceSettings',
              routeStatuses
            );
          }),
          catchError(() => {
            return of([]);
          })
        );
    }

    return this.maintenanceSettingsCache;
  }

  //Get status for given url(s)
  getRouteStatusForUrls(urls: string[]): Observable<Record<string, boolean>> {
    return this.getRouteStatuses().pipe(
      map((statuses: RouteStatus[]) => {
        const statusMap = statuses.reduce(
          (acc, status) => {
            if (urls.includes(status.url)) {
              acc[status.url] = status.isDisabled;
            }
            return acc;
          },
          {} as Record<string, boolean>
        );
        return statusMap;
      }),
      catchError(() => of({}))
    );
  }

  updateMaintenanceSettings(
    settings: RouteStatus[]
  ): Observable<DefaultResponse> {
    const payload = {
      requests: settings,
    };
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + '/Maintenance/maintenanceSettings/update',
      payload,
      httpOptions
    );
  }

  updateNavigationMaintenanceStatus(navigation: EntNavigation[]): void {
    const routeStatuses = this._localStorageService.getItem<RouteStatus[]>(
      'maintenanceSettings'
    );
    if (routeStatuses) {
      const globalDisable = routeStatuses.some(
        route => route.url === '/*' && route.isDisabled
      ); // When full maintenance mode
      // Assuming 'navigation' is an array you need to process
      navigation.forEach(navItem => {
        this.updateItemMaintenance(navItem, routeStatuses, globalDisable);
      });
    }
  }

  // Update path/item status when changing value
  private updateItemMaintenance(
    item: EntNavigation,
    routeStatuses: RouteStatus[],
    globalDisable: boolean
  ): void {
    // If the item has children, process each child recursively
    if (item.children && item.children.length > 0) {
      item.children.forEach(child =>
        this.updateItemMaintenance(child, routeStatuses, globalDisable)
      );
    }

    // Determine the maintenance status for the current item
    const routeStatus = routeStatuses.find(rs => rs.url === item.url);
    if (item.url === '/dashboard') {
      // Exclude /dashboard, keep it as false
      item.maintenance = false;
    } else {
      // Set maintenance based on routeStatus or globalDisable
      item.maintenance = globalDisable || routeStatus?.isDisabled || false;
    }
  }
}
